import { initializeApp } from 'firebase/app'
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  getRedirectResult,
  signInWithRedirect,
  signOut,
} from 'firebase/auth'
import { computed, onMounted, onUnmounted } from 'vue'
import { useSessionStorage } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { firebaseConfig } from './firebaseConfig'
import { brand } from '@/config/brands'
import { TALEWAVE_APP_GROUP_NAME, isTalewaveAppHost } from '@/config/brands/clientGroup'
import { useToastStore } from '@/stores'
import { api } from '@/api'
import { $formatErrorMessage, $getDeviceId, $getToken, $setToken } from '@/helpers'
import { useElementClick } from '@/composables/tracks'
import router from '@/router'
import { i18n } from '@/i18n'
import { getLoginTrackingRoute } from '@/utils/loginTrackingRoute'

type ILoginType = 'google.com' | 'facebook.com' | 'apple.com'
// 临时存储
const loginTypeTemp = useSessionStorage<any>('loginTypeTemp', undefined)
export function useTripartiteLogin(deviceUuid: string, loginToken: string, redirect: string) {
  initializeApp(firebaseConfig)
  const auth = getAuth()
  const skeleton = computed(() => !!loginTypeTemp.value)
  const { $toast } = useToastStore()
  const router = useRouter()
  const route = useRoute()
  const { elementClickForLogin } = useElementClick()

  onMounted(() => {
    // 往返缓存
    window.addEventListener('pageshow', (e) => {
      if (e.persisted)
        loginTypeTemp.value = undefined
    })
  })

  onUnmounted(() => {
    loginTypeTemp.value = undefined
  })

  // 只有在授权回来时, result才有值
  getRedirectResult(auth)
    .then((result: any) => {
      // eslint-disable-next-line no-console
      console.info('result', result)
      if (!result) {
        loginTypeTemp.value = undefined
        return
      }

      login(loginTypeTemp.value, result.user.providerData)
    }).catch((error) => {
      const errorCode = error.code
      const errorMessage = error.message
      const email = error.customData.email
      console.error('TripartiteLoginError', errorCode, errorMessage, email)
    })

  async function signInWithGoogle() {
    elementClickForLogin('login_google', {
      route_title: route.query.from,
      ...getLoginTrackingRoute(redirect),
    })
    const providerData = auth?.currentUser?.providerData || []
    const lastLoginHasGoogle = providerData?.filter((item: any) => item.providerId === 'google.com') || []

    if (lastLoginHasGoogle.length > 0) {
      login('google.com', lastLoginHasGoogle)
    }
    else {
      loginTypeTemp.value = 'google.com'
      const provider = new GoogleAuthProvider()
      signInWithRedirect(auth, provider)
    }
  }

  async function signInWithFacebook() {
    elementClickForLogin('login_fb', {
      route_title: route.query.from,
      ...getLoginTrackingRoute(redirect),
    })
    const providerData = auth?.currentUser?.providerData || []
    const lastLoginHasFacebook = providerData?.filter((item: any) => item.providerId === 'facebook.com') || []

    if (lastLoginHasFacebook.length > 0) {
      login('facebook.com', lastLoginHasFacebook)
    }
    else {
      loginTypeTemp.value = 'facebook.com'
      const provider = new FacebookAuthProvider()
      provider.addScope('email,public_profile')
      signInWithRedirect(auth, provider)
    }
  }

  async function signInWithApple() {
    elementClickForLogin('login_apple', {
      route_title: route.query.from,
      ...getLoginTrackingRoute(redirect),
    })
    const providerData = auth?.currentUser?.providerData || []
    const lastLoginHasApple = providerData?.filter((item: any) => item.providerId === 'apple.com') || []

    if (lastLoginHasApple.length > 0) {
      login('apple.com', lastLoginHasApple)
    }
    else {
      loginTypeTemp.value = 'apple.com'
      const provider = new OAuthProvider('apple.com')
      provider.addScope('email')
      provider.addScope('name')
      signInWithRedirect(auth, provider)
    }
  }

  async function logout() {
    if (auth && auth.currentUser)
      signOut(auth)
  }

  function getLoginType(type: ILoginType) {
    if (type === 'facebook.com')
      return '2'

    else if (type === 'google.com')
      return '1'

    else if (type === 'apple.com')
      return '3'
  }

  function preLogin(type: ILoginType, providerData: any) {
    const params = {
      thirdType: getLoginType(type),
      thirdPartId: '',
      nickName: '',
      email: '',
      headImage: '',
    }

    providerData.forEach((item: any) => {
      if (item.providerId === type) {
        params.thirdPartId = item.uid
        params.nickName = item.displayName
        params.email = item.email
        params.headImage = item.photoURL
      }
    })

    // apple 登录时, 没有昵称就用邮箱作为昵称
    if (type === 'apple.com' && params.email && !params.nickName)
      params.nickName = params.email

    return params
  }

  async function login(type: ILoginType, providerData: any) {
    const params = preLogin(type, providerData)

    if (!params?.thirdPartId || !params?.nickName || !params?.thirdType) {
      $toast('Login failed, please try again.')
      // logout()
      return
    }

    try {
      const res = await api.post<PostMemberBindThirdPartBody, PostMemberBindThirdPartResponse>('/member/bind/thirdPart', params, {
        headers: {
          equipmentId: deviceUuid,
          token: loginToken,
        },
      })
      if (res.code === 0) {
        if (redirect) {
          const url = new URL(redirect)
          url.searchParams.set('t', res.data.token)
          window.location.replace(url.toString())
        }
        else {
          $setToken(res.data.token)
          router.replace({ name: 'member' })
        }
      }
    }
    catch (error) {
      $toast($formatErrorMessage(error))
    }

    setTimeout(() => {
      loginTypeTemp.value = undefined
    }, 3000)
  }

  return {
    skeleton,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    logout,
  }
}

export function tripartiteLoginPage(title: string) {
  const d = $getDeviceId()
  const lang = i18n.locale
  // SuperStory 在当前站点完成登录，静态资源 CDN 不作为页面跳转地址。
  if (brand === 'superstory') {
    return router.push({
      name: 'login',
      query: { lang, d, t: $getToken(), from: title, redirect: window.location.href },
    })
  }
  // 登录收敛在固定域名，hostname 判断组别会失效，跳转时带 cg 参数标识组别
  const cg = isTalewaveAppHost() ? `&cg=${TALEWAVE_APP_GROUP_NAME}` : ''
  if (import.meta.env.DEV)
    router.replace({ name: 'login', query: { lang } })
  else
    window.location.href = `${import.meta.env.VITE_CDN}/login?redirect=${encodeURIComponent(window.location.href)}&d=${d}&t=${$getToken()}&from=${title}&lang=${encodeURIComponent(lang)}${cg}`
  // window.location.href = `https://h5-fb-local.rocnovel.com/login?redirect=${encodeURIComponent(window.location.href)}&d=${d}`
}
